import Chip from 'components/@extended/Chip';
import SegmentChip from 'components/@extended/SegmentChip.tsx';
import StatusChip from 'components/@extended/StatusChip';
import Tooltip from 'components/@extended/Tooltip';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import PermissionButton from 'components/buttons/PermissionButton';
import PinButton from 'components/buttons/PinButton';
import DmsLink from 'components/dmslink/DmsLink.tsx';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import type { DraftActions } from 'components/drafts/useDraftActions';
import { ArrowOutwardRounded as ArrowOutwardRoundedIcon, AttachMoney, BusinessCenter, Person } from 'components/icons';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import PageHeader from 'components/PageHeader';
import { OpponentChip } from 'components/party/OpponentChip';
import PartyName from 'components/party/PartyName';
import {
    DMS_META_CLASS,
    META_ROW_CLASS,
    MetaItem,
    PRIMARY_ACTION_CLASS,
    RecordMoreMenu,
} from 'components/record-header/RecordHeader';
import { Tooltip as KitTooltip } from 'components/ui/tooltip';
import { FormattedMessage } from 'react-intl';
import {
    useDeleteContactEmployeeHistoryEntryMutation,
    useGetContactEmployeeHistoryQuery,
    useUpdateContactEmployeeHistoryMutation,
} from 'store/api/contacts';
import { useDeleteContactMutation } from 'store/api/contacts';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { ClassifierData } from 'types/classifiers';
import { ContactDto, PartyType, RelationshipTypeCode } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { cn } from 'utils/cn';
import { getClassifierText, getIntl, t } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';
import { getClientNumber, isPartyNumberShownFor, partyNameString } from 'utils/partyNameUtil';
import { useCreateMatterPermissions } from 'utils/permissionUtil';
import { recordPin } from 'utils/pins';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import Blocked from '../matters/Blocked';
import OverdueInvoices from '../matters/OverdueInvoices';
import { getRelations } from './util';
import { useAdministratorField } from 'hooks/useAdministratorField';

type Classifiers = Record<string, ClassifierData>;

const SMALL_FONT = 'text-caption leading-[1.6667] font-medium';

const CASE_ADD_ENABLED_FOR_RELATIONSHIP_TYPES = [RelationshipTypeCode.CLIENT, RelationshipTypeCode.LEAD];

/** Party type, relationship, opponent, segment and service level. */
export const ContactChips = ({ contact, classifiers }: { contact: ContactDto; classifiers: Classifiers }) => (
    <div className="flex gap-1">
        {contact.partyType === PartyType.COMPANY ? (
            <KitTooltip content={t('contacts.types.company')}>
                <div>
                    <Chip size="small" label={<BusinessCenter />} type={'partyType'} />
                </div>
            </KitTooltip>
        ) : (
            <KitTooltip content={t('contacts.types.person')}>
                <div>
                    <Chip size="small" label={<Person />} type={'partyType'} />
                </div>
            </KitTooltip>
        )}
        {contact.relationshipTypeCode === RelationshipTypeCode.CLIENT ? (
            <KitTooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'CLIENT')}>
                <div>
                    <Chip size="small" label={<AttachMoney />} type={'partyType'} />
                </div>
            </KitTooltip>
        ) : null}
        {contact.relationshipTypeCode === RelationshipTypeCode.LEAD ? (
            <KitTooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'LEAD')}>
                <div>
                    <Chip size="small" label={<ArrowOutwardRoundedIcon />} type={'partyType'} />
                </div>
            </KitTooltip>
        ) : null}
        <OpponentChip state={contact.opponentCaseState} />
        {contact.segmentTypeCode != null && <SegmentChip segmentTypeCode={contact.segmentTypeCode} />}
        {contact.serviceLevelTypeCode != null && (
            <span className="text-subtitle2 text-grey-600 new:text-foreground-tertiary flex items-center font-semibold">
                {getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], contact.serviceLevelTypeCode)}
            </span>
        )}
    </div>
);

/** The client or party number, for the contacts and tenants that show one; otherwise null. */
const useShownPartyNumber = (contact: ContactDto): string | null => {
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const { data: currentTenant } = useGetCurrentTenantQuery();
    const clientNumber = getClientNumber(contact, isPartyNumberUsageEnabled);

    return isPartyNumberShownFor(currentTenant, contact.relationshipTypeCode) && clientNumber ? clientNumber : null;
};

/** The client or party number, for the contacts and tenants that show one. */
export const ContactPartyNumber = ({ contact }: { contact: ContactDto }) => {
    const clientNumber = useShownPartyNumber(contact);

    return clientNumber ? (
        <p className={SMALL_FONT}>
            <FormattedMessage
                id={contact.relationshipTypeCode === 'CLIENT' ? 'contacts.client-number' : 'contacts.party-number'}
                values={{ input: clientNumber }}
            />
        </p>
    ) : null;
};

/** The history of the contact's relationship partners or managers; a draft has none to show. */
const RelationHistoryButton = ({
    contact,
    fieldType,
    fieldNameId,
}: {
    contact: ContactDto;
    fieldType: HistoryFieldType;
    fieldNameId: string;
}) =>
    contact.isDraft ? null : (
        <DisplayHistoryButton
            entityType={HistoryEntityType.PARTY}
            entityId={`${contact.id}`}
            fieldType={fieldType}
            fieldName={t(fieldNameId)}
            activity={''}
            query={useGetContactEmployeeHistoryQuery}
            updateMutation={useUpdateContactEmployeeHistoryMutation}
            deleteMutation={useDeleteContactEmployeeHistoryEntryMutation}
        />
    );

/** Relationship partners and managers with their history, and the client assistants where the form has them. */
export const ContactRelations = ({ contact }: { contact: ContactDto }) => {
    const { isOn: isAdministratorFieldEnabled, clientLabel: clientAssistantLabel } = useAdministratorField();

    return (
        <div className={cn('flex items-center gap-2', SMALL_FONT)}>
            {contact?.partners?.length ? (
                <div className="flex items-center gap-[.5ch]">
                    <FormattedMessage id="contacts.relationship-partners" />:
                    {getRelations(contact, ['RELATIONSHIP_PARTNER'])}
                    <RelationHistoryButton
                        contact={contact}
                        fieldType={HistoryFieldType.RELATIONSHIP_PARTNER}
                        fieldNameId="contacts.relationship-partners"
                    />
                </div>
            ) : null}
            {contact?.managers?.length ? (
                <div className="flex items-center gap-[.5ch]">
                    <FormattedMessage id="contacts.relationship-managers" />:
                    {getRelations(contact, ['RELATIONSHIP_MANAGER'])}
                    <RelationHistoryButton
                        contact={contact}
                        fieldType={HistoryFieldType.RELATIONSHIP_MANAGER}
                        fieldNameId="contacts.relationship-managers"
                    />
                </div>
            ) : null}
            {isAdministratorFieldEnabled && contact?.administrators?.length ? (
                <div className="flex items-center gap-[.5ch]">
                    {clientAssistantLabel}:{getRelations(contact, ['PARTY_ADMINISTRATOR'])}
                </div>
            ) : null}
        </div>
    );
};

/** Overdue invoices and blocked cases or time entry, as icons. */
export const ContactStateIcons = ({ contact, className }: { contact: ContactDto; className?: string }) => {
    const isAddingNewCasesBlocked = contact.isAddingNewCasesBlocked ?? false;
    const isAddingTimecardsBlocked = contact.isAddingTimecardsBlocked ?? false;
    const blockingTooltipText =
        isAddingNewCasesBlocked && isAddingTimecardsBlocked
            ? 'contacts.combined-blocking-icon-tooltip'
            : isAddingNewCasesBlocked
              ? 'contacts.blocking-icon-tooltip'
              : isAddingTimecardsBlocked
                ? 'contacts.timecard-blocking-icon-tooltip'
                : null;

    return (
        <>
            {contact.revenueStatistics?.overdueAmount && contact.revenueStatistics.overdueAmount > 0 ? (
                <OverdueInvoices className={className} tooltipText="contacts.has-overdue-invoices" />
            ) : null}
            {blockingTooltipText && <Blocked className={className} tooltipText={blockingTooltipText} />}
        </>
    );
};

/** Add new case, for a client or lead the user may open cases for; disabled, with the reason, where the contact rules it out. */
export const AddCaseButton = ({
    contact,
    labelId = 'contacts.add-new-case',
    className,
}: {
    contact: ContactDto;
    labelId?: string;
    className?: string;
}) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isMatterCreationClientsOnly = useIsFeatureEnabled(FeatureFlag.MATTER_CREATION_CLIENTS_ONLY);
    const { canCreateForClients, canCreateForLeads } = useCreateMatterPermissions();

    const isAddNewCaseDisabledForRelationshipType = !CASE_ADD_ENABLED_FOR_RELATIONSHIP_TYPES.includes(
        contact.relationshipTypeCode
    );
    const isAddNewCaseDisabledForPersonaNonGrata = contact.isPersonaNonGrata;
    const isAddNewCaseDisabledForBlocked = contact.isAddingNewCasesBlocked;
    const isAddNewCaseDisabledForLeadRestriction =
        isMatterCreationClientsOnly && contact.relationshipTypeCode === RelationshipTypeCode.LEAD;

    const isAddNewCaseDisabled =
        isAddNewCaseDisabledForRelationshipType ||
        isAddNewCaseDisabledForPersonaNonGrata ||
        isAddNewCaseDisabledForBlocked ||
        isAddNewCaseDisabledForLeadRestriction;
    const addNewCaseDisabledTooltipText = isAddNewCaseDisabledForRelationshipType
        ? 'contacts.tooltips.add-new-case-disabled-non-client'
        : isAddNewCaseDisabledForPersonaNonGrata
          ? 'contacts.tooltips.add-new-case-disabled-persona-non-grata'
          : isAddNewCaseDisabledForBlocked
            ? 'contacts.tooltips.add-new-case-disabled-blocked'
            : isAddNewCaseDisabledForLeadRestriction
              ? 'contacts.tooltips.add-new-case-disabled-lead-restriction'
              : undefined;

    const canAddCaseForThisContact =
        contact.relationshipTypeCode === RelationshipTypeCode.CLIENT
            ? canCreateForClients
            : contact.relationshipTypeCode === RelationshipTypeCode.LEAD
              ? canCreateForLeads
              : false;

    if (!canAddCaseForThisContact) return null;
    return (
        // The tooltip hangs on the wrapper: the disabled button itself takes no pointer events.
        <Tooltip text={addNewCaseDisabledTooltipText}>
            <div>
                <PermissionButton
                    permissions={['CREATE.MATTERS', 'CREATE.MATTERS.LEADS', 'CREATE.MATTERS.CLIENTS']}
                    variant="contained"
                    color="secondary"
                    size="small"
                    className={className}
                    onClick={() => navigateWithReturn(`/contact/${contact.id}/matter/new`)}
                    disabled={isAddNewCaseDisabled}
                >
                    <FormattedMessage id={labelId} />
                </PermissionButton>
            </div>
        </Tooltip>
    );
};

interface Props {
    contact: ContactDto;
    classifiers: Classifiers;
    /** A draft's own actions, and the draft once loaded. */
    draftActions?: DraftActions;
}

/**
 * The contact header of the new design: number, chips and state on a first line, the full name, a meta
 * row that wraps, New case and a ⋯ menu with Delete. It shows what the classic header in `ContactPage` does,
 * except Edit: each section of the contact edits in place, and Delete moves here from the edit form.
 * A draft has the Draft chip where the number goes and no pin, who created it and when it was saved in the
 * meta row, and the draft's own actions.
 */
export default function ContactRecordHeader({ contact, classifiers, draftActions }: Props) {
    const draft = draftActions?.draft;
    const lastNameFirst = useLastNameFirst();
    const name = partyNameString(contact, { useOfficialName: true, lastNameFirst });
    const partyNumber = useShownPartyNumber(contact);
    const { isOn: isAdministratorFieldEnabled, clientLabel: clientAssistantLabel } = useAdministratorField();
    const { relationshipTypeCode } = contact;
    const isCompany = contact.partyType === PartyType.COMPANY;
    const servedBy =
        contact.servedByOffices &&
        getIntl().formatList(
            contact.servedByOffices.map((office) => office.name),
            { type: 'unit' }
        );
    const { openModal, closeModal } = useModal();
    const [deleteContact] = useDeleteContactMutation();
    const leaveDeleted = useLeaveDeletedEntity('/contacts');

    // The edit form's Delete: the same gate and request, behind a confirmation that names the contact, then away
    // from the deleted record.
    const confirmDelete = () =>
        openModal(
            <ConfirmationModal
                titleId="contacts.delete-title"
                titleValues={{ name }}
                confirmId="contacts.delete"
                onCancel={closeModal}
                onConfirm={() => {
                    void deleteContact({ id: `${contact.id}` })
                        .unwrap()
                        .then(() => leaveDeleted())
                        .catch(console.error)
                        .finally(() => closeModal());
                }}
                content={<p className="text-body1">{t('contacts.delete-body')}</p>}
            />
        );

    return (
        <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-auto flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    {partyNumber ? (
                        <span className="text-foreground-tertiary text-meta font-mono">[{partyNumber}]</span>
                    ) : null}
                    {contact.isDraft ? <DraftChip /> : null}
                    {relationshipTypeCode === RelationshipTypeCode.CLIENT ||
                    relationshipTypeCode === RelationshipTypeCode.LEAD ? (
                        <StatusChip
                            size="small"
                            type="success"
                            label={getClassifierText(classifiers['RELATIONSHIP_TYPE'], relationshipTypeCode)}
                        />
                    ) : null}
                    <StatusChip
                        size="small"
                        type="neutral"
                        label={t(isCompany ? 'contacts.types.company' : 'contacts.types.person')}
                    />
                    <OpponentChip state={contact.opponentCaseState} />
                    <ContactStateIcons contact={contact} />
                    {contact.isDraft ? null : <PinButton pin={recordPin('contact', contact.id, name)} />}
                </div>
                <PageHeader
                    // A draft may be saved before it has a name.
                    title={name || t('new-entity.contact')}
                    titleNode={
                        name ? (
                            <PartyName party={contact} variant="readonly-self" personaNonGrataIconSize={24} />
                        ) : undefined
                    }
                    showBreadcrumb
                >
                    <div className={META_ROW_CLASS}>
                        {isCompany && contact.regCode ? (
                            <MetaItem label={<FormattedMessage id="reg-number" />}>
                                <span className="text-meta font-mono">{contact.regCode}</span>
                            </MetaItem>
                        ) : null}
                        {contact.partners?.length ? (
                            <MetaItem label={<FormattedMessage id="contacts.relationship-partners" />}>
                                {getRelations(contact, ['RELATIONSHIP_PARTNER'])}
                                <RelationHistoryButton
                                    contact={contact}
                                    fieldType={HistoryFieldType.RELATIONSHIP_PARTNER}
                                    fieldNameId="contacts.relationship-partners"
                                />
                            </MetaItem>
                        ) : null}
                        {contact.managers?.length ? (
                            <MetaItem label={<FormattedMessage id="contacts.relationship-managers" />}>
                                {getRelations(contact, ['RELATIONSHIP_MANAGER'])}
                                <RelationHistoryButton
                                    contact={contact}
                                    fieldType={HistoryFieldType.RELATIONSHIP_MANAGER}
                                    fieldNameId="contacts.relationship-managers"
                                />
                            </MetaItem>
                        ) : null}
                        {isAdministratorFieldEnabled && contact.administrators?.length ? (
                            <MetaItem label={clientAssistantLabel}>
                                {getRelations(contact, ['PARTY_ADMINISTRATOR'])}
                            </MetaItem>
                        ) : null}
                        {servedBy ? (
                            <MetaItem label={<FormattedMessage id="contacts.served-by-offices" />}>{servedBy}</MetaItem>
                        ) : null}
                        {contact.segmentTypeCode != null ? (
                            <MetaItem label={<FormattedMessage id="contacts.segment-type" />}>
                                {getClassifierText(classifiers['SEGMENT_TYPE'], contact.segmentTypeCode)}
                            </MetaItem>
                        ) : null}
                        {contact.serviceLevelTypeCode != null ? (
                            <MetaItem label={<FormattedMessage id="contacts.service-level-type" />}>
                                {getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], contact.serviceLevelTypeCode)}
                            </MetaItem>
                        ) : null}
                        {contact.dmsLink ? (
                            <span className={DMS_META_CLASS}>
                                <DmsLink link={contact.dmsLink} label={t('open-in-dms')} isIconDisplayed={true} />
                            </span>
                        ) : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </div>
                </PageHeader>
            </div>
            <div className="flex flex-none items-center justify-end gap-2">
                {contact.isDraft ? (
                    draftActions && <DraftRecordActions actions={draftActions} />
                ) : (
                    <>
                        <AddCaseButton contact={contact} labelId="contacts.new-case" className={PRIMARY_ACTION_CLASS} />
                        <RecordMoreMenu
                            menuItems={
                                contact.isDeletable
                                    ? [{ handler: confirmDelete, title: t('delete'), variant: 'destructive' }]
                                    : []
                            }
                        />
                    </>
                )}
            </div>
        </div>
    );
}
